import { extractApiErrorMessage } from '@cartwheel/api'
import {
  type ReassignmentDetail,
  type ReassignmentStatus,
  reassignmentByIdQueryKey,
  reassignmentsQueryKey,
  useUpdateReassignmentMutation,
} from '@cartwheel/api/v1'
import type { ReassignmentEditValues } from '@cartwheel/components'
import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

type ReassignmentAction = Exclude<ReassignmentStatus, 'initiated'>

const EMPTY_EDIT_VALUES: ReassignmentEditValues = {
  effective_date: '',
  reason: '',
  notes: '',
}

const buildEditValues = (data: ReassignmentDetail | null | undefined): ReassignmentEditValues => ({
  effective_date: data?.effective_date ?? '',
  reason: (data?.reasons?.[0] as ReassignmentEditValues['reason']) ?? '',
  notes: data?.notes ?? '',
})

/**
 * Hook for managing reassignment actions (approve, deny, cancel) and edits
 * @param reassignmentId - ID of the reassignment to act on
 * @param onSuccess - Callback invoked after successful action mutation (typically closes modal)
 */
export function useReassignmentActions(reassignmentId: string | null, onSuccess: () => void) {
  const queryClient = useQueryClient()
  const [selectedAction, setSelectedAction] = useState<ReassignmentAction | ''>('')
  const [additionalDetails, setAdditionalDetails] = useState('')
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [isEditing, setIsEditing] = useState(false)
  const [editValues, setEditValues] = useState<ReassignmentEditValues>(EMPTY_EDIT_VALUES)
  const [initialEditValues, setInitialEditValues] =
    useState<ReassignmentEditValues>(EMPTY_EDIT_VALUES)
  const [editError, setEditError] = useState<string | null>(null)

  const updateMutation = useUpdateReassignmentMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reassignmentsQueryKey() })
      resetForm()
      onSuccess()
      reloadPageWithToast()
    },
    onError: (error: unknown) => {
      setSubmitError(
        extractApiErrorMessage(error, 'An error occurred while updating the reassignment')
      )
    },
  })

  const editMutation = useUpdateReassignmentMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reassignmentsQueryKey() })
      if (reassignmentId) {
        queryClient.invalidateQueries({
          queryKey: reassignmentByIdQueryKey({ path: { id: reassignmentId } }),
        })
      }
      setIsEditing(false)
      setEditError(null)
    },
    onError: (error: unknown) => {
      setEditError(extractApiErrorMessage(error, 'An error occurred while saving the reassignment'))
    },
  })

  const resetForm = () => {
    setSelectedAction('')
    setAdditionalDetails('')
    setSubmitError(null)
    setIsEditing(false)
    setEditError(null)
  }

  const startEdit = (data: ReassignmentDetail | null | undefined) => {
    const values = buildEditValues(data)
    setEditValues(values)
    setInitialEditValues(values)
    setEditError(null)
    setIsEditing(true)
  }

  const cancelEdit = () => {
    setIsEditing(false)
    setEditError(null)
  }

  const reloadPageWithToast = () => {
    const actionMessage = (selectedAction: ReassignmentAction | '') => {
      if (selectedAction === '') return ''

      const actionToMessage: Record<ReassignmentAction, string> = {
        approved: 'Approved',
        denied: 'Denied',
        assigned_to_cc: 'Assigned to CC',
        cancelled: 'Cancelled',
      }

      return actionToMessage[selectedAction]
    }

    const url = new URL(window.location.href)
    url.searchParams.set('success', `Reassignment Request ${actionMessage(selectedAction)}`)
    window.location.assign(url.toString())
  }

  const handleSubmit = () => {
    if (!selectedAction || !reassignmentId) return

    setSubmitError(null)
    updateMutation.mutate({
      path: { id: reassignmentId },
      body: {
        reassignment_action: selectedAction as ReassignmentAction,
        notes: additionalDetails || undefined,
      },
    })
  }

  const handleSaveEdit = () => {
    if (!reassignmentId) return

    if (
      editValues.effective_date === initialEditValues.effective_date &&
      editValues.reason === initialEditValues.reason &&
      editValues.notes === initialEditValues.notes
    ) {
      setIsEditing(false)
      setEditError(null)
      return
    }

    setEditError(null)
    editMutation.mutate({
      path: { id: reassignmentId },
      body: {
        effective_date: editValues.effective_date || undefined,
        reason: editValues.reason || undefined,
        notes: editValues.notes || undefined,
      },
    })
  }

  return {
    selectedAction,
    setSelectedAction,
    additionalDetails,
    setAdditionalDetails,
    submitError,
    handleSubmit,
    resetForm,
    isPending: updateMutation.isPending,
    isEditing,
    editValues,
    setEditValues,
    startEdit,
    cancelEdit,
    handleSaveEdit,
    isSaving: editMutation.isPending,
    editError,
  }
}
